import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Spinner,
  Table,
  Pagination
} from 'react-bootstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import axios from '../api/axios';
import PropTypes from 'prop-types';

const DATE_FILTER_OPTIONS = [
  { label: 'Today',       value: 'today' },
  { label: 'Yesterday',   value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7days' },
  { label: 'Last 30 Days',value: 'last30days' },
  { label: 'This Month',  value: 'thisMonth' },
  { label: 'Last Month',  value: 'lastMonth' },
];

const ProductsKPI = ({ outlets, loadingOutlets }) => {

  const token = localStorage.getItem('authToken');

  const [deactivatedProductsList, setDeactivatedProductsList] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState('');
  const [totalPages, setTotalPages] = useState('');

  // State to hold the raw API data
  const [rawTopSellingProducts, setRawTopSellingProducts] = useState([]);
  const [rawTopNonSellingProducts, setRawTopNonSellingProducts] = useState([]);
  const [rawTopLeastSellingProducts, setRawTopLeastSellingProducts] = useState([]);

  // State for filtered data used in charts
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [topNonSellingProducts, setTopNonSellingProducts] = useState([]);
  const [topLeastSellingProducts, setTopLeastSellingProducts] = useState([]);

  // Filters
  const [outletFilter, setOutletFilter] = useState('all');
  const [classTypeFilter, setClassTypeFilter] = useState('all');
  const [foodTypeFilter, setFoodTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');  

  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [deactivatedLoading, setDeactivatedLoading] = useState(false);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    getDeactivatedProducts(newPage, limit);
  };

  const handleRowsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setLimit(newLimit);
    setPage(1); // reset to first page
    getDeactivatedProducts(1, newLimit);
  };

  // Fetch Deactivated Products Api
  const getDeactivatedProducts = async(page , limit) => {
    setDeactivatedLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      }
      const response = await axios.get(`dashboard/getDeactivatedProducts?page=${page}&limit=${limit}`, { headers });
      console.log("Response :", response?.data?.data);
      setDeactivatedProductsList(response?.data?.data);
      setTotalPages(response?.data?.pagination?.totalPages);
      setTotalRecords(response?.data?.pagination?.total);
    } catch (error) {
      console.log("Error in Deactivated Products :", error);
    } finally {
      setDeactivatedLoading(false);
    }
  }

  // Fetch API data from the endpoint with filters
  const fetchProductsKIPData = async (outlet, cType, fType, dateF) => {
    try {
      setLoadingProducts(true);
      setLoadingFilter(true);
      
      let url = `dashboard/getProductsStatThroughSummary`
      const params = new URLSearchParams();

      if (outlet !== 'all') {
        params.append('outletId', outlet);
      }
      if (cType !== 'all') {
        params.append('classType', cType);
      }
      if (fType !== 'all') {
        params.append('foodType', fType);
      }
      if (dateF) {
        params.append('dateFilter', dateF);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("Response of Product KPI's :", response);
      
      setRawTopSellingProducts(response?.data?.data?.data?.topTenSellingProducts || []);
      setRawTopNonSellingProducts(response?.data?.data?.data?.topTenNonSellingProducts || []);
      setRawTopLeastSellingProducts(response?.data?.data?.data?.topTenLeastSellingProducts || []);
    } catch (error) {
      console.error('Error fetching products data:', error);
    } finally {
      setLoadingProducts(false);
      setLoadingFilter(false);
    }
  };

  const handleOutletChange = (e) => {
    const outId = e.target.value;
    setOutletFilter(outId);
    fetchProductsKIPData(outId, classTypeFilter, foodTypeFilter, dateFilter);
  };

  const handleClassTypeChange = (e) => {
    const cType = e.target.value;
    setClassTypeFilter(cType);
    fetchProductsKIPData(outletFilter, cType, foodTypeFilter, dateFilter);
  };

  const handleFoodTypeChange = (e) => {
    const fType = e.target.value;
    setFoodTypeFilter(fType);
    fetchProductsKIPData(outletFilter, classTypeFilter, fType, dateFilter);
  };

  const handleDateFilterChange = (e) => {
    const dateF = e.target.value;
    setDateFilter(dateF);
    fetchProductsKIPData(outletFilter, classTypeFilter, foodTypeFilter, dateF);
  };

  // Fetch the API data on initial component mount
  useEffect(() => {
    fetchProductsKIPData(outletFilter, classTypeFilter, foodTypeFilter, dateFilter);
    getDeactivatedProducts(page, limit);
  }, []);

  // Apply client-side filtering if needed
  useEffect(() => {
    setTopSellingProducts(rawTopSellingProducts);
    setTopNonSellingProducts(rawTopNonSellingProducts);
    setTopLeastSellingProducts(rawTopLeastSellingProducts);
  }, [rawTopSellingProducts, rawTopNonSellingProducts, rawTopLeastSellingProducts]);

  const resetFilters = () => {
    setOutletFilter('all');
    setClassTypeFilter('all');
    setFoodTypeFilter('all');
    setDateFilter('today');
    fetchProductsKIPData('all', 'all', 'all', 'today');
  };

  return (
    <Container fluid className="py-4 shadow-sm" style={{ backgroundColor: 'white' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h4 className='fw-bold mb-0'>PRODUCT ANALYSIS</h4>
        <div className="d-flex align-items-center bg-light p-1 ps-2 rounded">
          <i className="bi bi-calendar-event me-2 text-primary" />
          <Form.Select
            value={dateFilter}
            onChange={handleDateFilterChange}
            style={{ width: '145px' }}
            className="border-0 bg-light"
          >
            {DATE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </div>
      </div>
      <hr />
      
      {/* Filters */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label className='fw-semibold text-muted'>Outlet</Form.Label>
              <Form.Select
                value={outletFilter}
                onChange={handleOutletChange}
                disabled={loadingFilter || loadingOutlets}
              >
                <option value="all">All Outlets</option>
                {outlets?.map((outlet) => (
                  <option key={outlet._id} value={outlet._id}>
                    {outlet.name}
                  </option>
                ))}
              </Form.Select>
          </Form.Group>
        </Col>
        
        <Col md={3}>
          <Form.Group>
            <Form.Label className='fw-semibold text-muted'>Class Type</Form.Label>
            <Form.Select
              value={classTypeFilter}
              onChange={handleClassTypeChange}
              disabled={loadingFilter}
            >
              <option value="all">All</option>
              <option value="FOOD">Food</option>
              <option value="BEVERAGE">Beverage</option>
            </Form.Select>
          </Form.Group>
        </Col>
        
        <Col md={3} >
          <Form.Group>
            <Form.Label className='fw-semibold text-muted'>Food Type</Form.Label>
            <Form.Select
              value={foodTypeFilter}
              onChange={handleFoodTypeChange}
              disabled={loadingFilter}
            >
              <option value="all">All</option>
              <option value="VEG">Veg</option>
              <option value="NON-VEG">Non-Veg</option>
            </Form.Select>
          </Form.Group>
        </Col>
        
        <Col md={3} className="d-flex align-items-end">
          <button 
            className="btn btn-outline-secondary w-100"
            onClick={resetFilters}
            disabled={loadingFilter}
          >
            Reset Filters
          </button>
        </Col>
      </Row>

      {loadingProducts ? (
        <Row className="justify-content-center my-5">
          <Col md="auto">
            <div className="d-flex flex-column align-items-center">
              <Spinner animation="border" variant="primary" />
              <span className="mt-2">Loading product data...</span>
            </div>
          </Col>
        </Row>
      ) : (
        <>
          {/* Top Selling and Least Selling Products Charts - Side by Side */}
          <Row>
            <Col md={6}>
              <h5>Top 10 Selling Products</h5>
              
              {topSellingProducts?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={topSellingProducts}
                    margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="productName"
                      interval={0}
                      tick={{
                        fontSize: 10,
                        angle: -45,
                        textAnchor: 'end',
                        fill: '#555'
                      }}
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="totalSaleCount"
                      fill="#8884d8"
                      className='fw-semibold'
                      name="Ordered Count"
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '300px' }}>
                  <div className="text-muted">No data available</div>
                </div>
              )}
            </Col>
            
            <Col md={6}>
              <h5>Top 10 Least Selling Products</h5>
              {topLeastSellingProducts?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={topLeastSellingProducts}
                    margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="productName"
                      interval={0}
                      tick={{
                        fontSize: 10,
                        angle: -45,
                        textAnchor: 'end',
                        fill: '#555'
                      }}
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="totalSaleCount"
                      fill="#ffc658"
                      className='fw-semibold'
                      name="Ordered Count"
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '300px' }}>
                  <div className="text-muted">No data available</div>
                </div>
              )}
            </Col>
          </Row>

          {/* Top Non-Selling Products Table - Below */}
          <Row className="mt-4">
            <Col md={12}>
              <h5>Top 10 Non-Selling Products</h5>
              {topNonSellingProducts?.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered hover className="mt-1">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Product Name</th>
                        <th>Category</th>
                        <th>Class Type</th>
                        <th>Food Type</th>
                        <th>Ordered Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topNonSellingProducts?.map((product, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <span className='fw-semibold text-secondary'>{product?.name}</span>
                          </td>
                          <td style={{ color: 'purple' }}>{product?.category?.name}</td>
                          <td>{product?.classType || '-'}</td>
                          <td>{product?.foodType || '-'}</td>
                          <td>{product?.orderedCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '100px' }}>
                  <div className="text-muted">No non-selling products data available</div>
                </div>
              )}
            </Col>
          </Row>

          {/* Deactivated Products Table - Below */}
          <Row className="mt-2">
            <Col md={12}>
              <h5>Deactivated Products</h5>
              {deactivatedProductsList?.length > 0 ? (
                <div className="table-responsive">
                  
                  <Table striped bordered hover className="mt-1">
                    <thead className="table-danger">
                      <tr>
                        <th>#</th>
                        <th>Deactivated On</th>
                        <th>Product Name</th>
                        <th>Category</th>
                        <th>Class Type</th>
                        <th>Food Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deactivatedProductsList?.map((product, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{new Date(product?.updatedAt).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </td>

                          <td>
                            <span className='fw-semibold text-secondary'>{product?.name}</span>
                          </td>
                          <td style={{ color: 'purple' }}>{product?.category?.name}</td>
                          <td>{product?.classType || '-'}</td>
                          <td>{product?.foodType || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 text-muted">Show</span>
                      <Form.Select style={{ width: 'auto' }} value={limit} onChange={handleRowsPerPageChange}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Form.Select>
                      <span className="ms-2 text-muted">entries</span>
                    </div>

                    <div className="d-flex align-items-center">
                      <Pagination className="mb-0">
                        <Pagination.Prev disabled={page === 1} onClick={() => handlePageChange(page - 1)} />
                        {Array.from({ length: totalPages }, (_, i) => (
                          <Pagination.Item key={i + 1} active={i + 1 === page} onClick={() => handlePageChange(i + 1)}>
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === totalPages} onClick={() => handlePageChange(page + 1)} />
                      </Pagination>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '100px' }}>
                  <div className="text-muted">No deactivated products data available</div>
                </div>
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

ProductsKPI.propTypes = {
  outlets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string
  })),
  loadingOutlets: PropTypes.bool
};

ProductsKPI.defaultProps = {
  outlets: [],
  loadingOutlets: false
};

export default ProductsKPI;